import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  Timestamp,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { getDb, getRealtimeManager } from '@talk2nick/shared';
import type { Convo, ConvoMember, Message, User } from '@talk2nick/core';

import { markRead, sortKeyOf } from './messages';

/** 읽음 반영 디바운스 (PLAN 5-8장 — stampede 방지) */
const MARK_READ_DEBOUNCE_MS = 3_000;

export interface ConvoListItem {
  id: string;
  convo: Convo;
}

// ── 대화 목록 — 앱 전체에서 리스너 1개 (PLAN 5-8-9장) ─────────────────
//
// 목록 화면, 전달 대상 시트, 인앱 알림 배너가 모두 같은 데이터를 필요로 한다.
// 각자 onSnapshot을 걸면 같은 쿼리를 2~3번 구독하게 되어 읽기 비용이 배로 늘므로,
// 모듈 싱글턴으로 하나만 만들고 useSyncExternalStore로 나눠 쓴다.

interface ConvoListSnapshot {
  items: ConvoListItem[];
  loading: boolean;
}

const EMPTY_LIST: ConvoListSnapshot = { items: [], loading: false };
const listListeners = new Set<() => void>();
let listUid: string | null = null;
let listSnapshot: ConvoListSnapshot = { items: [], loading: true };
let listUnsub: (() => void) | null = null;

function emitList(): void {
  for (const listener of listListeners) listener();
}

function ensureConvoList(uid: string | null): void {
  if (listUid === uid) return;
  listUid = uid;
  listUnsub?.();
  listUnsub = null;

  if (!uid) {
    listSnapshot = EMPTY_LIST;
    emitList();
    return;
  }

  listSnapshot = { items: [], loading: true };
  emitList();

  const q = query(
    collection(getDb(), 'convos'),
    where('memberIds', 'array-contains', uid),
    orderBy('updatedAt', 'desc'),
    limit(50),
  );

  listUnsub = onSnapshot(
    q,
    (snap) => {
      listSnapshot = {
        items: snap.docs.map((d) => ({ id: d.id, convo: d.data() as Convo })),
        loading: false,
      };
      emitList();
    },
    (error: unknown) => {
      console.error('[talk2nIcK] 대화방 목록 구독 실패:', error);
      listSnapshot = { items: [], loading: false };
      emitList();
    },
  );
}

/**
 * 내가 속한 대화방 목록 (최근 순).
 * `memberIds` array-contains + `updatedAt` 정렬은 복합 인덱스가 필요하다
 * (firestore.indexes.json). `enabled=false`면 구독을 시작하지 않는다.
 */
export function useConvoList(uid: string | null, enabled = true) {
  const subscribe = useCallback((cb: () => void) => {
    listListeners.add(cb);
    return () => {
      listListeners.delete(cb);
    };
  }, []);
  const getSnapshot = useCallback(() => listSnapshot, []);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_LIST);

  useEffect(() => {
    if (!enabled) return;
    ensureConvoList(uid);
  }, [enabled, uid]);

  return snapshot;
}

/** 단일 대화방 문서 구독 — 방장 화면(제목·설정·adminIds)이 필요로 한다 */
export function useConvo(convoId: string | null): Convo | null {
  const [convo, setConvo] = useState<Convo | null>(null);

  useEffect(() => {
    if (!convoId) {
      setConvo(null);
      return;
    }
    const unsubscribe = onSnapshot(
      doc(getDb(), 'convos', convoId),
      (snap) => setConvo(snap.exists() ? (snap.data() as Convo) : null),
      (error: unknown) => {
        console.error('[talk2nIcK] 대화방 구독 실패:', error);
      },
    );
    return unsubscribe;
  }, [convoId]);

  return convo;
}

/**
 * 활성 대화방 메시지 구독 (PLAN 5-8-9장).
 *
 * RealtimeManager를 거쳐 **활성 방 1개**만 구독한다. 로컬 write는
 * persistentLocalCache가 즉시 스냅샷에 넣어 주므로 낙관적 UI가 공짜로 나온다.
 * 정렬은 clientCreatedAt 기준 — serverTimestamp가 아직 없는(전송 중) 메시지도
 * 제자리를 찾도록 하기 위함이다.
 */
export function useActiveMessages(convoId: string | null) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!convoId) {
      setMessages([]);
      setPendingIds(new Set());
      return;
    }
    const manager = getRealtimeManager();
    setLoading(true);

    // 활성 방은 1개만 — manager가 이전 구독을 스스로 정리한다
    manager.subscribeActiveConvo(
      convoId,
      () => {},
      (docs) => {
        const nextPending = new Set<string>();
        const list: Message[] = docs.map((item) => {
          const raw = item.data as Record<string, unknown>;
          const clientCreatedAt =
            typeof raw.clientCreatedAt === 'number' ? raw.clientCreatedAt : Date.now();
          const createdAt =
            raw.createdAt && typeof (raw.createdAt as { toDate?: unknown }).toDate === 'function'
              ? (raw.createdAt as Timestamp)
              : Timestamp.fromMillis(clientCreatedAt);

          if (item.hasPendingWrites) {
            nextPending.add(item.id);
          }

          return {
            ...(raw as unknown as Omit<
              Message,
              'id' | 'convoId' | 'createdAt' | 'clientCreatedAt'
            >),
            createdAt,
            clientCreatedAt,
            id: item.id,
            convoId,
          } as Message;
        });

        list.sort((a, b) => sortKeyOf(a) - sortKeyOf(b));
        setMessages(list);
        setPendingIds(nextPending);
        setLoading(false);
      },
    );

    return () => {
      manager.teardown();
      setMessages([]);
      setPendingIds(new Set());
    };
  }, [convoId]);

  return { messages, pendingIds, loading };
}

/** 대화방 멤버(역할·lastReadAt) 구독 — 틱과 헤더 인원수에 쓴다 */
export function useConvoMembers(convoId: string | null, enabled = true) {
  const [members, setMembers] = useState<Map<string, ConvoMember>>(new Map());

  useEffect(() => {
    if (!convoId || !enabled) {
      if (!enabled) return;
      setMembers(new Map());
      return;
    }
    const unsubscribe = onSnapshot(
      collection(getDb(), 'convos', convoId, 'members'),
      (snap) => {
        const next = new Map<string, ConvoMember>();
        for (const d of snap.docs) next.set(d.id, d.data() as ConvoMember);
        setMembers(next);
      },
      (error: unknown) => {
        console.error('[talk2nIcK] 멤버 구독 실패:', error);
      },
    );
    return unsubscribe;
  }, [convoId, enabled]);

  return members;
}

/** 단일 멤버 구독 — 그룹 대화방에서 전체 멤버 컬렉션을 구독하지 않고 내 상태나 특정 상대만 타겟 구독 (PLAN v0.2 Phase 4) */
export function useConvoMember(convoId: string | null, uid: string | null): ConvoMember | null {
  const [member, setMember] = useState<ConvoMember | null>(null);

  useEffect(() => {
    if (!convoId || !uid) {
      setMember(null);
      return;
    }
    const unsubscribe = onSnapshot(
      doc(getDb(), 'convos', convoId, 'members', uid),
      (snap) => setMember(snap.exists() ? (snap.data() as ConvoMember) : null),
      (error: unknown) => {
        console.error('[talk2nIcK] 멤버 단일 구독 실패:', error);
      },
    );
    return unsubscribe;
  }, [convoId, uid]);

  return member;
}

/** 상대 프로필 구독 — DM 제목/아바타를 실시간으로 계산하려면 필요하다 (PLAN 3장) */
export function useUserProfile(uid: string | null) {
  const [profile, setProfile] = useState<User | null>(null);

  useEffect(() => {
    if (!uid) {
      setProfile(null);
      return;
    }
    const unsubscribe = onSnapshot(
      doc(getDb(), 'users', uid),
      (snap) => setProfile(snap.exists() ? (snap.data() as User) : null),
      (error: unknown) => {
        console.error('[talk2nIcK] 프로필 구독 실패:', error);
      },
    );
    return unsubscribe;
  }, [uid]);

  return profile;
}

/**
 * 읽음 처리 — 마지막 메시지 시각까지 lastReadAt을 올린다.
 * 3초 디바운스 + 값이 커질 때만 쓴다 (stampede 방지).
 */
export function useMarkRead(
  convoId: string | null,
  uid: string | null,
  lastMessageMs: number,
): void {
  const writtenRef = useRef(0);

  useEffect(() => {
    if (!convoId || !uid || lastMessageMs <= 0) return;
    if (lastMessageMs <= writtenRef.current) return;

    const timer = window.setTimeout(() => {
      writtenRef.current = lastMessageMs;
      void markRead(convoId, uid, lastMessageMs).catch((error: unknown) => {
        console.error('[talk2nIcK] 읽음 처리 실패:', error);
      });
    }, MARK_READ_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [convoId, uid, lastMessageMs]);
}
